// Default record-permission role keys for the PROJECT module. These match the
// `record_permission_roles.key` values seeded for system roles. roleKey is now
// a free-form string column on ProjectMember — these constants are the canonical
// set the UI exposes by default; orgs can add custom roles via the
// record-permissions module.
export const PROJECT_ROLE_KEY = {
  PROJECT_ADMIN: 'project_admin',
  PROJECT_MANAGER: 'project_manager',
  LEAD: 'lead',
  CONTRIBUTOR: 'contributor',
  REVIEWER: 'reviewer',
  STAKEHOLDER: 'stakeholder',
  READ_ONLY: 'read_only',
} as const;

export type ProjectRoleKey = (typeof PROJECT_ROLE_KEY)[keyof typeof PROJECT_ROLE_KEY];

/**
 * @deprecated Use `PROJECT_ROLE_KEY` (lowercase record-permission role keys).
 * Retained as an alias so older imports keep compiling — values now match
 * `PROJECT_ROLE_KEY` directly so equality checks against persisted roleKey
 * strings still work.
 */
export const ProjectRoleEnum = {
  PROJECT_ADMIN: 'project_admin',
  PROJECT_MANAGER: 'project_manager',
  LEAD: 'lead',
  CONTRIBUTOR: 'contributor',
  REVIEWER: 'reviewer',
  STAKEHOLDER: 'stakeholder',
  READONLY: 'read_only',
} as const;

export type ProjectRoleEnum = (typeof ProjectRoleEnum)[keyof typeof ProjectRoleEnum];

export const PROJECT_ROLE_LABELS: Record<ProjectRoleKey, string> = {
  [PROJECT_ROLE_KEY.PROJECT_ADMIN]: 'Project Admin',
  [PROJECT_ROLE_KEY.PROJECT_MANAGER]: 'Project Manager',
  [PROJECT_ROLE_KEY.LEAD]: 'Lead',
  [PROJECT_ROLE_KEY.CONTRIBUTOR]: 'Contributor',
  [PROJECT_ROLE_KEY.REVIEWER]: 'Reviewer',
  [PROJECT_ROLE_KEY.STAKEHOLDER]: 'Stakeholder',
  [PROJECT_ROLE_KEY.READ_ONLY]: 'Read Only',
};

export const PROJECT_ROLE_DESCRIPTIONS: Record<ProjectRoleKey, string> = {
  [PROJECT_ROLE_KEY.PROJECT_ADMIN]: 'Full control over project settings, members, and access policies',
  [PROJECT_ROLE_KEY.PROJECT_MANAGER]: 'Manage members, planning, and tab features',
  [PROJECT_ROLE_KEY.LEAD]: 'Can manage team members, approve time entries, and oversee work',
  [PROJECT_ROLE_KEY.CONTRIBUTOR]: 'Can work on tasks, update status, log time',
  [PROJECT_ROLE_KEY.REVIEWER]: 'Can review work, approve changes, comment',
  [PROJECT_ROLE_KEY.STAKEHOLDER]: 'View-only access to track progress',
  [PROJECT_ROLE_KEY.READ_ONLY]: 'Read-only access to specific areas',
};

export const PROJECT_ROLE_PROPERTIES: Record<ProjectRoleKey, { readonly: boolean }> = {
  [PROJECT_ROLE_KEY.PROJECT_ADMIN]: { readonly: false },
  [PROJECT_ROLE_KEY.PROJECT_MANAGER]: { readonly: false },
  [PROJECT_ROLE_KEY.LEAD]: { readonly: false },
  [PROJECT_ROLE_KEY.CONTRIBUTOR]: { readonly: false },
  [PROJECT_ROLE_KEY.REVIEWER]: { readonly: false },
  [PROJECT_ROLE_KEY.STAKEHOLDER]: { readonly: false },
  [PROJECT_ROLE_KEY.READ_ONLY]: { readonly: true },
};

export interface ProjectMemberWithRole {
  id: string;
  userId: string;
  projectId: string;
  roleKey: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: Date;
  updatedAt: Date;
  user: {
    id: string;
    email: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    image?: string;
  };
}
